import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSEO from '@/hooks/useSEO';

const allServices = [
  {
    id: 'frameless-shower-doors',
    title: 'Frameless Shower Doors',
    category: 'Shower',
    headline: 'Frameless Shower Doors Las Vegas',
    desc: 'Transform your bathroom with a custom frameless glass shower door. Sleek, modern, and built to last — our frameless enclosures are the gold standard in luxury bathroom design.',
    features: ['Tempered safety glass', 'Custom sizing', 'Multiple hardware finishes', 'Easy-clean coating'],
    img: 'https://public.readdy.ai/ai/img_res/41572b599c336addc1509afd3d5f4711.jpg',
    popular: true,
  },
  {
    id: 'sliding-glass-doors',
    title: 'Sliding Glass Doors',
    category: 'Shower',
    headline: 'Sliding Glass Shower Doors Las Vegas',
    desc: 'Space-saving sliding glass doors with smooth, whisper-quiet glide systems. Perfect for smaller bathrooms or anyone who prefers a sleek, modern look.',
    features: ['Smooth glide hardware', 'Space-saving design', 'Custom track systems', 'Frosted or clear glass'],
    img: 'https://readdy.ai/api/search-image?query=modern%20sliding%20glass%20shower%20door%20with%20brushed%20nickel%20hardware%2C%20clear%20glass%20panels%2C%20contemporary%20bathroom%20design%2C%20clean%20white%20background%2C%20professional%20photo&width=800&height=600&seq=srv002&orientation=landscape',
    popular: false,
  },
  {
    id: 'tub-enclosures',
    title: 'Tub Enclosures',
    category: 'Shower',
    headline: 'Glass Tub Enclosures Las Vegas',
    desc: 'Custom glass enclosures for bathtubs that are elegant, watertight, and easy to maintain. Available in sliding, hinged, or fixed panel configurations.',
    features: ['Watertight seals', 'Hinged or sliding options', 'Custom dimensions', 'Low-iron glass available'],
    img: 'https://readdy.ai/api/search-image?query=luxury%20glass%20bathtub%20enclosure%20with%20clear%20tempered%20glass%20panels%2C%20freestanding%20tub%2C%20marble%20floor%2C%20modern%20bathroom%20interior%2C%20professional%20architectural%20photography&width=800&height=600&seq=srv003&orientation=landscape',
    popular: false,
  },
  {
    id: 'glass-railings',
    title: 'Glass Railings',
    category: 'Railings',
    headline: 'Glass Railings Las Vegas',
    desc: 'Frameless glass railings for stairs, balconies, and decks. Combine safety with stunning aesthetics — let the view speak for itself.',
    features: ['Frameless or semi-frameless', 'Stainless steel posts', 'Interior & exterior', 'Code compliant'],
    img: 'https://readdy.ai/api/search-image?query=modern%20frameless%20glass%20stair%20railing%20in%20luxury%20home%20interior%2C%20clear%20tempered%20glass%20panels%20with%20stainless%20steel%20posts%2C%20open%20concept%20living%20space%2C%20professional%20interior%20design%20photo&width=800&height=600&seq=srv004&orientation=landscape',
    popular: true,
  },
  {
    id: 'stair-glass',
    title: 'Stair Glass',
    category: 'Railings',
    headline: 'Stair Glass Panels Las Vegas',
    desc: 'Custom glass panels for staircases that create an open, airy feel. Available in clear, frosted, or tinted glass to match your interior design.',
    features: ['Custom panel sizing', 'Clear or frosted options', 'Structural glass', 'Modern aesthetic'],
    img: 'https://readdy.ai/api/search-image?query=modern%20glass%20staircase%20with%20clear%20glass%20panels%20and%20metal%20handrail%20in%20contemporary%20home%2C%20open%20staircase%20design%2C%20natural%20light%2C%20luxury%20interior%20architecture&width=800&height=600&seq=srv005&orientation=landscape',
    popular: false,
  },
  {
    id: 'custom-mirrors',
    title: 'Custom Mirrors',
    category: 'Mirrors',
    headline: 'Custom Mirrors Las Vegas',
    desc: 'Bespoke mirrors cut to any size and shape. Perfect for bathrooms, gyms, salons, and feature walls. We fabricate and install with precision.',
    features: ['Any size or shape', 'Beveled edges available', 'Frameless or framed', 'Gym & salon mirrors'],
    img: 'https://readdy.ai/api/search-image?query=large%20custom%20frameless%20mirror%20in%20modern%20bathroom%20with%20LED%20lighting%2C%20clean%20white%20walls%2C%20minimalist%20design%2C%20luxury%20interior%2C%20professional%20photography&width=800&height=600&seq=srv006&orientation=landscape',
    popular: true,
  },
  {
    id: 'glass-partitions',
    title: 'Glass Partitions',
    category: 'Commercial',
    headline: 'Glass Partitions Las Vegas',
    desc: 'Office and home glass partitions that create open, light-filled spaces. Available in clear, frosted, or decorative glass for any application.',
    features: ['Full or partial height', 'Frosted for privacy', 'Sliding or fixed', 'Commercial grade'],
    img: 'https://readdy.ai/api/search-image?query=modern%20glass%20office%20partition%20wall%20with%20frosted%20glass%20panels%2C%20contemporary%20workspace%20design%2C%20natural%20light%2C%20minimalist%20interior%2C%20professional%20architectural%20photo&width=800&height=600&seq=srv007&orientation=landscape',
    popular: false,
  },
  {
    id: 'window-glass',
    title: 'Window Glass Replacement',
    category: 'Commercial',
    headline: 'Window Glass Replacement Las Vegas',
    desc: 'Fast, professional window glass replacement for homes and businesses. We handle single-pane, double-pane, and specialty glass replacements.',
    features: ['Same-day service available', 'Double-pane insulated', 'Energy efficient options', 'Emergency repairs'],
    img: 'https://readdy.ai/api/search-image?query=large%20floor%20to%20ceiling%20windows%20in%20modern%20home%20with%20clear%20glass%20panels%2C%20natural%20light%20flooding%20interior%2C%20contemporary%20architecture%2C%20professional%20photography&width=800&height=600&seq=srv008&orientation=landscape',
    popular: false,
  },
];

const categories = ['All', 'Shower', 'Railings', 'Mirrors', 'Commercial'];

const ServicesPage = () => {
  useSEO({
    title: 'Glass Services Las Vegas | Shower Doors, Railings, Mirrors | Quinolux',
    description: 'Custom glass installation in Las Vegas — frameless shower doors, sliding glass doors, tub enclosures, glass railings, stair glass, custom mirrors & glass partitions. Free estimates. Call (702) 604-2093.',
    keywords: 'glass services Las Vegas, frameless shower doors Las Vegas, sliding shower doors Las Vegas, tub enclosures Las Vegas, glass railings Las Vegas, glass stair railing Las Vegas, custom mirrors Las Vegas, glass partitions Las Vegas, shower door installation Las Vegas, shower door replacement Las Vegas',
    canonical: 'https://quinoluxglass.com/services',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Glass Services Las Vegas | Quinolux Glass & Mirrors',
        description: 'Custom glass installation services in Las Vegas — frameless shower doors, glass railings, custom mirrors, tub enclosures, and more.',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/services`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/` },
            { '@type': 'ListItem', position: 2, name: 'Services', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/services` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Glass Services by Quinolux',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Frameless Shower Doors Las Vegas', url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/services` },
          { '@type': 'ListItem', position: 2, name: 'Sliding Glass Doors Las Vegas', url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/services` },
          { '@type': 'ListItem', position: 3, name: 'Tub Enclosures Las Vegas', url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/services` },
          { '@type': 'ListItem', position: 4, name: 'Glass Railings Las Vegas', url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/services` },
          { '@type': 'ListItem', position: 5, name: 'Custom Mirrors Las Vegas', url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/services` },
          { '@type': 'ListItem', position: 6, name: 'Glass Partitions Las Vegas', url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/services` },
          { '@type': 'ListItem', position: 7, name: 'Window Glass Replacement Las Vegas', url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/services` },
        ],
      },
    ],
  });

  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? allServices
    : allServices.filter(s => s.category === activeCategory);

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0A0A0A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20glass%20texture%20with%20light%20reflections%20and%20refractions%2C%20luxury%20material%20background%2C%20dark%20tones%2C%20professional%20studio%20photography&width=1920&height=600&seq=svchero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">What We Offer</span>
          <h1 className="text-5xl md:text-6xl font-light text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Our Glass Services
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            From frameless shower doors to custom mirrors and glass railings — premium glass installations for homes and businesses across Las Vegas.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <a
              href="tel:+17026042093"
              className="inline-flex items-center gap-3 bg-white text-black text-sm font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              Call (702) 604-2093
            </a>
            <a
              href="#quote"
              className="inline-flex items-center gap-3 border border-gray-600 text-white text-sm font-medium px-8 py-4 rounded-full hover:border-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="bg-white border-b border-gray-100 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filtered.map((svc) => (
              <div key={svc.id} className="group rounded-2xl overflow-hidden border border-gray-100 hover:border-gray-200 transition-all duration-300">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={svc.img}
                    alt={svc.headline}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  {svc.popular && (
                    <div className="absolute top-4 left-4 bg-black text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-white/90 text-black text-xs font-medium px-3 py-1 rounded-full">
                    {svc.category}
                  </div>
                </div>
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-black mb-2">{svc.title}</h2>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{svc.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {svc.features.map((f) => (
                      <span key={f} className="bg-gray-50 text-gray-600 text-xs px-3 py-1 rounded-full border border-gray-100">
                        {f}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href="tel:+17026042093"
                      className="flex-1 flex items-center justify-center gap-2 bg-black text-white text-sm font-medium py-3 rounded-xl hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line"></i>
                      Call Now
                    </a>
                    <a
                      href="#quote"
                      className="flex-1 flex items-center justify-center gap-2 border border-gray-200 text-black text-sm font-medium py-3 rounded-xl hover:border-black transition-colors duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Get Quote
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Text */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-light text-black mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Custom Glass Installation in Las Vegas
            </h2>
            <p className="text-gray-500 text-sm">From frameless shower doors to custom mirrors and glass railings — premium glass installations for homes and businesses across Las Vegas.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Frameless Shower Doors</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Transform your bathroom with a custom frameless glass shower door. Sleek, modern, and built to last — our frameless enclosures are the gold standard in luxury bathroom design.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Sliding Glass Doors</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Space-saving sliding glass doors with smooth, whisper-quiet glide systems. Perfect for smaller bathrooms or anyone who prefers a sleek, modern look.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Tub Enclosures</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Custom glass enclosures for bathtubs that are elegant, watertight, and easy to maintain. Available in sliding, hinged, or fixed panel configurations.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Glass Railings</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Frameless glass railings for stairs, balconies, and decks. Combine safety with stunning aesthetics — let the view speak for itself.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Stair Glass</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Custom glass panels for staircases that create an open, airy feel. Available in clear, frosted, or tinted glass to match your interior design.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Custom Mirrors</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Bespoke mirrors cut to any size and shape. Perfect for bathrooms, gyms, salons, and feature walls. We fabricate and install with precision.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Glass Partitions</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Office and home glass partitions that create open, light-filled spaces. Available in clear, frosted, or decorative glass for any application.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Window Glass Replacement</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Fast, professional window glass replacement for homes and businesses. We handle single-pane, double-pane, and specialty glass replacements.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <QuoteFormSection />

      <Footer />
    </main>
  );
};

const QuoteFormSection = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d7eoj25fi84lst2k0890', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-20 bg-[#F8F8F6]">
      <div className="max-w-2xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-light text-black mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Get Your Free Estimate
          </h2>
          <p className="text-gray-500 text-sm">No obligation. We&apos;ll respond within 24 hours.</p>
        </div>
        {submitted ? (
          <div className="bg-white rounded-3xl p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-black text-white mx-auto mb-4">
              <i className="ri-check-line text-2xl"></i>
            </div>
            <h3 className="text-black font-bold text-xl mb-2">Request Received!</h3>
            <p className="text-gray-500 text-sm">We&apos;ll contact you within 24 hours with your free estimate.</p>
          </div>
        ) : (
          <form
            data-readdy-form
            id="services-quote-form"
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-8 flex flex-col gap-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Full Name *</label>
                <input type="text" name="name" required value={formData.name} onChange={handleChange} placeholder="John Smith" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Phone *</label>
                <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} placeholder="(702) 000-0000" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Service Needed *</label>
              <select name="service" required value={formData.service} onChange={handleChange} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors cursor-pointer">
                <option value="">Select a service...</option>
                <option>Frameless Shower Doors</option>
                <option>Sliding Glass Doors</option>
                <option>Tub Enclosures</option>
                <option>Glass Railings</option>
                <option>Stair Glass</option>
                <option>Custom Mirrors</option>
                <option>Glass Partitions</option>
                <option>Window Glass Replacement</option>
                <option>Handyman Services</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Message</label>
              <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Describe your project..." rows={4} maxLength={500} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors resize-none" />
            </div>
            <button type="submit" disabled={submitting} className="w-full bg-black text-white text-sm font-semibold py-4 rounded-xl hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60">
              {submitting ? 'Sending...' : 'Get My Free Estimate'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default ServicesPage;
